import { useEffect, useMemo, useRef, useState } from 'react';
import type { Area } from '@cg-projects/shared';
import { api } from '@/core/api/apiClient';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useDashboardStore } from '@/features/dashboard/store/dashboardStore';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { useToastStore } from '@/core/ui/toastStore';
import { useUiStore } from '@/core/ui/uiStore';
import { Avatar, CountPill, Kbd } from '@/core/ui/atoms';
import { Icon } from '@/core/ui/icons';
import {
  BarChart3,
  Bell,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Image,
  Inbox,
  LayoutGrid,
  LifeBuoy,
  LogOut,
  Moon,
  Plus,
  Search,
  Settings,
  Star,
  Sun,
  Target,
  X,
} from 'lucide-react';
import { initials } from '@/core/ui/helpers';
import { onUserNotify } from '@/core/realtime/socket';
import { setTheme, useTheme } from '@/core/ui/theme';

/** Everything a new hire needs to know, one ? away. Rendered by LAYOUT, not
 *  this sidebar: the sidebar is display:none'd below md (and the phone drawer
 *  copy is unmounted until opened), so a popover rendered here was invisible
 *  exactly when '?' was pressed on a small screen — position:fixed does not
 *  escape a display:none ancestor. */
export function HelpPopover({ onClose }: { onClose: () => void }) {
  const ROWS: Array<[string, string]> = [
    ['Ctrl/Cmd + K', 'Search every task you can see'],
    ['G then I · M · H · R · D', 'Go to Inbox, My Work, Home, Reports, or Media'],
    ['/', 'Jump to the search box on a board'],
    ['?', 'Show or hide these tips'],
    ['C', 'New task in the first stage (board view)'],
    ['Esc', 'Close any panel or dialog'],
    ['Ctrl/Cmd + click a card', 'Select tasks for bulk edit'],
    ['Shift + click a card', 'Select every task between your last two clicks'],
    ['Right-click a task', 'Quick actions — complete, move, assign, due dates'],
    ['Ctrl/Cmd + Enter', 'Send a comment'],
    ['@ in a comment', 'Mention someone (pings their inbox)'],
    ['Ctrl/Cmd + V on a task', 'Attach a pasted screenshot'],
    ['Drag a card', 'Move it between stages'],
    ['Drag a calendar chip', 'Reschedule its date'],
    ['Double-click the project name', 'Rename it (leads/admins)'],
    ['Copy link on a task', 'Share a direct link to it'],
  ];
  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div className="fixed bottom-14 left-3 z-50 w-72 overflow-hidden rounded-xl border border-line bg-canvas shadow-2xl animate-fade-in">
        <div className="flex items-center justify-between border-b border-line px-3 py-2">
          <span className="text-xs font-semibold text-ink">Tips & shortcuts</span>
          <button onClick={onClose} className="text-faint hover:text-ink"><Icon i={X} size={14} /></button>
        </div>
        <div className="max-h-80 overflow-y-auto p-2">
          {ROWS.map(([k, v]) => (
            <div key={k} className="flex items-start gap-2 px-1.5 py-1">
              <span className="shrink-0 rounded bg-hover px-1.5 py-0.5 text-[10px] font-medium text-brand">{k}</span>
              <span className="text-[11px] leading-snug text-muted">{v}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// --- Sidebar IA: PINNED / RECENT / ALL --------------------------------------
// 38 projects made the flat list a scan-every-time affair. Pins are the user's
// own curation, Recent is the trail of the last five projects they opened here,
// and All keeps the complete alphabetical list one fold below. Both rings are
// per-browser localStorage; every read/write is lazy and guarded, so the Node
// test runner (no window) never touches storage at module scope.

const PINS_KEY = 'cgp:pins';
const RECENT_KEY = 'cgp:recent-areas';
const ALL_OPEN_KEY = 'cgp:all-open';
const RECENT_MAX = 5;

function readIds(key: string): number[] {
  try {
    const v = JSON.parse(localStorage.getItem(key) ?? '[]') as unknown;
    // A malformed ring (schema drift, hand-edited storage) reads as empty —
    // the sidebar must never crash over a localStorage value.
    return Array.isArray(v) ? v.filter((n): n is number => typeof n === 'number') : [];
  } catch { return []; }
}
function writeIds(key: string, ids: number[]) {
  try { localStorage.setItem(key, JSON.stringify(ids)); } catch { /* best-effort */ }
}

/** The section header idiom — the old "Projects · N" label, reused verbatim. */
const SECTION_LABEL = 'px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wide text-faint';

/** One project row. The pin star is a SIBLING of the row button (nested buttons
 *  are invalid HTML and browsers un-nest them, breaking both click targets) —
 *  absolutely positioned into the right gutter the row's pr-7 reserves, so the
 *  star fading in on hover never shifts the name or the late count. */
function ProjectRow({ area, active, late, pinned, onSelect, onTogglePin }: {
  area: Area; active: boolean; late: number | undefined; pinned: boolean;
  onSelect: () => void; onTogglePin: () => void;
}) {
  return (
    <div className="group relative">
      <button
        onClick={onSelect}
        className={`flex w-full items-center gap-2 rounded-md py-1.5 pl-2 pr-7 text-left text-[13px] transition ${
          active ? 'bg-active font-medium text-ink' : 'text-muted hover:bg-hover hover:text-ink'
        }`}
      >
        <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${area.isContentTeam ? 'bg-brand' : 'bg-(--color-area-dot)'}`} />
        <span className="min-w-0 flex-1 truncate">{area.name}</span>
        {/* Quiet per-project overdue count, read from the Home feed already in
            memory — the sidebar never fetches it. No data / zero = nothing. */}
        {late !== undefined && (
          <span className="shrink-0 text-2xs tabular-nums text-danger" title={`${late} overdue`}>{late}</span>
        )}
      </button>
      <button
        onClick={onTogglePin}
        title={pinned ? 'Unpin' : 'Pin to the top'}
        className={`absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 opacity-0 transition focus-visible:opacity-100 group-hover:opacity-100 ${
          pinned ? 'text-brand' : 'text-faint hover:text-brand'
        }`}
      >
        <Icon i={Star} size={14} className={pinned ? 'fill-current' : ''} />
      </button>
    </div>
  );
}

export function AreaSidebar() {
  const areas = useAreaStore((s) => s.areas);
  const loading = useAreaStore((s) => s.loading);
  const error = useAreaStore((s) => s.error);
  const selectedAreaId = useAreaStore((s) => s.selectedAreaId);
  const selectArea = useAreaStore((s) => s.selectArea);
  const goHome = useAreaStore((s) => s.goHome);
  const openCreateProject = useUiStore((s) => s.openCreateProject);
  const openRequestDoor = useUiStore((s) => s.openRequestDoor);
  const openInbox = useUiStore((s) => s.openInbox);
  const openSettings = useUiStore((s) => s.openSettings);
  const openMyWork = useUiStore((s) => s.openMyWork);
  const openReports = useUiStore((s) => s.openReports);
  const openMedia = useUiStore((s) => s.openMedia);
  const inboxOpen = useUiStore((s) => s.inboxOpen);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const openSearch = useUiStore((s) => s.openSearch);
  const [filter, setFilter] = useState('');
  // Pins + recents survive reload. Lazy initializers so the storage read
  // happens in the browser, once; the handlers below write straight through.
  const [pins, setPins] = useState<number[]>(() => readIds(PINS_KEY));
  const [recentIds, setRecentIds] = useState<number[]>(() => readIds(RECENT_KEY));
  const [allOpen, setAllOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(ALL_OPEN_KEY) !== '0'; } catch { return true; }
  });
  const [unread, setUnread] = useState(0);
  // Browser notifications work across ANY Chrome tab (Destiny's ask) — but only
  // once the user grants permission, which must be triggered by a click.
  const [notifPerm, setNotifPerm] = useState<NotificationPermission | 'unsupported'>(
    typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
  );
  const enableNotifs = () => {
    if (typeof Notification === 'undefined') return;
    void Notification.requestPermission().then(async (p) => {
      setNotifPerm(p);
      // The phone release: with permission granted, also register this DEVICE
      // for pushes — so a mention reaches a pocket, not just an open tab.
      // Ships-dark aware: no server key = no subscription, silently.
      if (p !== 'granted' || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
      try {
        const { key } = await api.get<{ key: string | null }>('/auth/push-key');
        if (!key) return;
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
        await api.post('/auth/push-subscribe', { subscription: sub.toJSON() });
      } catch { /* best-effort — in-app + email still deliver */ }
    });
  };
  // State lives in uiStore and the popover renders from Layout (see HelpPopover's
  // doc comment) — this button only flips the flag.
  const toggleHelp = useUiStore((s) => s.toggleHelp);
  // Shared with the Settings → My account control via useTheme, so either
  // control flipping it updates this one in the same tab.
  const theme = useTheme();
  const [profileOpen, setProfileOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const toast = useToastStore((s) => s.show);
  const reloadAssignees = useLookupStore((s) => s.reloadAssignees);
  const setAvatarUrl = useLookupStore((s) => s.setAvatarUrl);
  const loadLookups = useLookupStore((s) => s.load);
  useEffect(() => { void loadLookups(); }, [loadLookups]);

  const uploadPhoto = async (f: File) => {
    if (uploading) return;
    if (!f.type.startsWith('image/')) { toast('That file is not an image.', 'error'); return; }
    if (f.size > 4_000_000) { toast('Photo too large — keep it under 4 MB.', 'error'); return; }
    setUploading(true);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const rd = new FileReader();
        rd.onload = () => resolve(String(rd.result).split(',')[1] ?? '');
        rd.onerror = () => reject(new Error('read failed'));
        rd.readAsDataURL(f);
      });
      const r = await api.post<{ avatarUrl: string | null }>('/assignees/me/avatar', { dataBase64 }, { timeout: 60000 });
      // Use the CACHE-BUSTED url the server returns so the sidebar avatar changes
      // string → its Avatar re-renders and shows the new photo now, not on reload.
      if (user && r?.avatarUrl) setAvatarUrl(user.uid, r.avatarUrl);
      await reloadAssignees();
      if (user && r?.avatarUrl) setAvatarUrl(user.uid, r.avatarUrl); // reload returns the plain url — re-bust
      toast('Profile photo updated — it shows everywhere your name does.', 'info');
      setProfileOpen(false);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not upload the photo.', 'error');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // Unread project messages — badge without opening the inbox. Slow poll as a
  // baseline, an INSTANT refetch on any personal ping, refresh on inbox close.
  useEffect(() => {
    if (inboxOpen) return;
    let alive = true;
    // Light endpoint: ONE Odoo search_count, not the full inbox page (list +
    // author resolution) that the badge never renders.
    const check = () => api.get<{ unread: number }>('/inbox/unread-count?scope=projects')
      .then((r) => { if (alive) setUnread(r.unread); })
      .catch(() => undefined);
    void check();
    const t = setInterval(() => void check(), 90_000);
    // Odoo needs a beat to write the notification row before we recount. Debounce
    // so a burst of pings collapses into ONE recount, not one per ping.
    let pingT: ReturnType<typeof setTimeout> | undefined;
    const off = onUserNotify(() => { if (pingT) clearTimeout(pingT); pingT = setTimeout(() => { if (alive) void check(); }, 1500); });
    return () => { alive = false; clearInterval(t); if (pingT) clearTimeout(pingT); off(); };
  }, [inboxOpen]);

  // The browser tab shows the unread count — visible from any other tab.
  useEffect(() => {
    document.title = unread > 0 ? `(${unread > 99 ? '99+' : unread}) CG Projects` : 'CG Projects';
  }, [unread]);

  const togglePin = (id: number) => {
    const next = pins.includes(id) ? pins.filter((x) => x !== id) : [...pins, id];
    writeIds(PINS_KEY, next);
    setPins(next);
  };
  const toggleAllOpen = () => {
    const next = !allOpen;
    try { localStorage.setItem(ALL_OPEN_KEY, next ? '1' : '0'); } catch { /* best-effort */ }
    setAllOpen(next);
  };
  /** Every sidebar project click funnels through here so RECENT keeps itself. */
  const pick = (id: number) => {
    const next = [id, ...recentIds.filter((x) => x !== id)].slice(0, RECENT_MAX);
    writeIds(RECENT_KEY, next);
    setRecentIds(next);
    selectArea(id);
  };

  // Per-project overdue counts, READ ONLY from the dashboard feed the Home
  // screen already loaded — the sidebar itself never fetches. Before Home's
  // first load (a cold project deep link) the map is empty and rows just
  // show no count.
  const dashProjects = useDashboardStore((s) => s.data?.projects);
  const lateByArea = useMemo(() => {
    const m = new Map<number, number>();
    for (const p of dashProjects ?? []) if (p.overdue > 0) m.set(p.areaId, p.overdue);
    return m;
  }, [dashProjects]);

  // One filter, three sections: the query narrows PINNED, RECENT and ALL alike.
  const sections = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const match = (a: Area) => !q || a.name.toLowerCase().includes(q);
    const pinSet = new Set(pins);
    const byId = new Map(areas.map((a) => [a.id, a] as const));
    return {
      q,
      // Pinned keeps the list's own (alphabetical) order — consistent scanning.
      pinnedAreas: areas.filter((a) => pinSet.has(a.id) && match(a)),
      // Recent is newest-first, skips pinned (already one glance up) and drops
      // anything access revoked since (absent from `areas`) without ceremony.
      recentAreas: recentIds
        .map((id) => byId.get(id))
        .filter((a): a is Area => !!a && !pinSet.has(a.id) && match(a)),
      allAreas: areas.filter(match),
    };
  }, [areas, filter, pins, recentIds]);
  // A collapsed ALL must never hide filter matches — a live query forces it open.
  const allSectionOpen = sections.q ? true : allOpen;

  return (
    <aside className="flex h-dvh w-64 shrink-0 flex-col border-r border-line bg-sidebar">
      {/* Brand — click to go Home. The login screen's wordmark (LoginScreen.tsx),
          at sidebar scale: ChronicGuru stacked over a tracked PROJECTS caps label. */}
      <button onClick={goHome} className="flex h-14 shrink-0 items-center gap-2 px-3 text-left transition hover:opacity-90" title="Home">
        <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded bg-brand text-xs font-bold text-page">
          CG
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-[15px] font-bold leading-5 tracking-tight text-ink">
            Chronic<span className="text-brand">Guru</span>
          </span>
          <span className="text-[9px] font-semibold uppercase leading-3 tracking-[0.22em] text-muted">
            Projects
          </span>
        </span>
      </button>

      {/* Filter */}
      <div className="px-3 pb-2">
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search projects…"
          className="w-full rounded-md border border-line bg-canvas px-2.5 py-1.5 text-xs text-ink outline-none placeholder:text-faint focus:border-brand"
        />
      </div>

      {/* Nav — ONE borderless stack on the project-row recipe (DESIGN_DIRECTION
          Wave 1.4). Text + fill + monochrome icons; the border-pill era is over. */}
      <nav className="space-y-0.5 px-2 pb-2 pt-1">
        {/* The door back to the rest of CG (Allyson, 8/7: "no way to get back
            to all apps"). A plain same-tab anchor: leaving Projects IS the
            intent, and the .cg-prose new-tab handler never touches chrome. */}
        <a
          href="https://team.chronicguru.com"
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted transition hover:bg-hover hover:text-ink"
          title="Back to all CG apps — clock, inventory, accounting, everything"
        >
          <Icon i={LayoutGrid} /> All CG apps
        </a>
        {/* My Work — your personal cross-project view. The one brand-colored row. */}
        <button
          // Wrapped, not passed by reference: openMyWork now takes an optional
          // view, and onClick would hand it the MouseEvent — a truthy object that
          // would sail into setMode and break the toggle. The type checker caught it.
          onClick={() => openMyWork()}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium text-brand transition hover:bg-hover"
          title="Everything assigned to you, across all projects"
        >
          <Icon i={Target} /> My Work
        </button>

        {/* Reports — keyword + filters across everything you can see, with export */}
        <button
          onClick={() => openReports()}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted transition hover:bg-hover hover:text-ink"
          title="Search by keyword across projects, filter, and download a spreadsheet"
        >
          <Icon i={BarChart3} /> Reports
        </button>

        {/* Media — the shared library: every photo/video/file on tasks you can
            see, plus library uploads scoped the same way. */}
        <button
          onClick={openMedia}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted transition hover:bg-hover hover:text-ink"
          title="Media — photos, videos and files from your projects, plus shared uploads"
        >
          <Icon i={Image} /> Media
        </button>

        {/* Global search — discoverable, not just a secret shortcut. Dressed as an
            input (border + canvas fill — inputs keep their borders) with a Kbd hint. */}
        <button
          onClick={openSearch}
          className="flex w-full items-center gap-2 rounded-md border border-line bg-canvas px-2 py-1.5 text-left text-sm text-faint transition hover:border-brand/50 hover:text-muted"
          title="Search every task you can see"
        >
          <Icon i={Search} /> Search everything <Kbd className="ml-auto">Ctrl+K</Kbd>
        </button>

        <button
          onClick={openCreateProject}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted transition hover:bg-hover hover:text-ink"
        >
          <Icon i={Plus} /> New
        </button>

        <button
          onClick={openRequestDoor}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted transition hover:bg-hover hover:text-ink"
          title="Need something from another team? Ask — it lands on their board, they hear about it, and you'll hear back."
        >
          <Icon i={LifeBuoy} /> Ask a team
        </button>

        <button
          onClick={openInbox}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted transition hover:bg-hover hover:text-ink"
          title="Inbox — your message archive"
        >
          <Icon i={Inbox} /> Inbox
          {unread > 0 && <CountPill value={unread > 99 ? '99+' : unread} tone="brand" className="ml-auto" />}
        </button>

        {/* Everyone — Settings now carries each person's OWN account tab (email
            preferences); admins additionally get the people-&-access tabs. */}
        {(
          <button
            onClick={openSettings}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted transition hover:bg-hover hover:text-ink"
            title={user?.isAdmin ? 'Settings — people & project access' : 'Settings — your account'}
          >
            <Icon i={Settings} /> Settings
          </button>
        )}

        {/* Enable browser notifications (only until granted) */}
        {notifPerm === 'default' && (
          <button
            onClick={enableNotifs}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted transition hover:bg-hover hover:text-ink"
            title="Get notified in any Chrome tab when you're assigned, mentioned, or a task is approved"
          >
            <Icon i={Bell} /> Enable notifications
          </button>
        )}
      </nav>

      {/* Projects — PINNED (your curation), RECENT (your last five), ALL (the
          complete list, collapsible). Empty sections don't render, so a fresh
          user still sees one plain list under one header. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {loading && <div className="px-2 py-2 text-xs text-muted">Loading…</div>}
        {error && <div className="px-2 py-2 text-xs text-danger">{error}</div>}
        {!loading && !error && areas.length === 0 && (
          <div className="mx-2 mt-1 rounded-md border border-dashed border-line px-2.5 py-3 text-xs leading-relaxed text-faint">
            No projects yet. You'll see your projects here as soon as Pat or Allyson adds you — nothing is broken.
          </div>
        )}

        {sections.pinnedAreas.length > 0 && (
          <div className="mb-2">
            <div className={SECTION_LABEL}>Pinned</div>
            {sections.pinnedAreas.map((a) => (
              <ProjectRow
                key={a.id}
                area={a}
                active={a.id === selectedAreaId}
                late={lateByArea.get(a.id)}
                pinned
                onSelect={() => pick(a.id)}
                onTogglePin={() => togglePin(a.id)}
              />
            ))}
          </div>
        )}

        {sections.recentAreas.length > 0 && (
          <div className="mb-2">
            <div className={SECTION_LABEL}>Recent</div>
            {sections.recentAreas.map((a) => (
              <ProjectRow
                key={a.id}
                area={a}
                active={a.id === selectedAreaId}
                late={lateByArea.get(a.id)}
                pinned={false}
                onSelect={() => pick(a.id)}
                onTogglePin={() => togglePin(a.id)}
              />
            ))}
          </div>
        )}

        {areas.length > 0 && (
          <div>
            <button
              onClick={toggleAllOpen}
              className={`${SECTION_LABEL} flex w-full items-center gap-1 text-left transition hover:text-muted`}
              title={allSectionOpen ? 'Collapse the full list' : 'Show the full list'}
            >
              <Icon i={allSectionOpen ? ChevronDown : ChevronRight} size={14} />
              All · {areas.length}
            </button>
            {allSectionOpen && sections.allAreas.map((a) => (
              <ProjectRow
                key={a.id}
                area={a}
                active={a.id === selectedAreaId}
                late={lateByArea.get(a.id)}
                pinned={pins.includes(a.id)}
                onSelect={() => pick(a.id)}
                onTogglePin={() => togglePin(a.id)}
              />
            ))}
          </div>
        )}

        {!loading && areas.length > 0 && sections.allAreas.length === 0 && (
          <div className="px-2 py-2 text-xs text-faint">No projects match.</div>
        )}
      </div>


      {/* User — click the avatar to set a profile photo */}
      <div className="relative flex shrink-0 items-center gap-2 border-t border-line px-3 py-2.5">
        <button onClick={() => setProfileOpen((v) => !v)} title="Profile photo" className="shrink-0 transition hover:opacity-80">
          {user ? (
            <Avatar person={{ id: user.uid, name: user.name, avatarUrl: null }} size={28} />
          ) : (
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-active text-[11px] font-medium text-ink">?</span>
          )}
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium text-ink">{user?.name}</div>
          {/* The version is its OWN chip, outside the truncating email: it used to sit
              at the end of the same truncate div, so any normal-length email rendered
              it as "· v1…" — surfacing the version is a standing rule, and a version
              you can't read isn't surfaced. shrink-0 = the email gives way, never the
              version. */}
          <div className="flex items-center gap-1.5 text-[10px] text-faint">
            <span className="min-w-0 truncate">{user?.email}</span>
            <span
              className="shrink-0 rounded bg-hover px-1 py-px font-medium tabular-nums text-muted"
              title="App version — matches the changelog"
            >
              v{__APP_VERSION__}
            </span>
          </div>
        </div>
        <button
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
          title={theme === 'light' ? 'Switch to the dark look' : 'Switch to the light look'}
          className="rounded p-1 text-faint transition hover:bg-hover hover:text-ink"
        >
          <Icon i={theme === 'light' ? Moon : Sun} />
        </button>
        <button
          onClick={toggleHelp}
          title="Tips & shortcuts"
          className="rounded p-1 text-faint transition hover:bg-hover hover:text-ink"
        >
          <Icon i={HelpCircle} />
        </button>
        <button
          onClick={logout}
          title="Sign out"
          className="rounded p-1 text-faint transition hover:bg-hover hover:text-ink"
        >
          <Icon i={LogOut} />
        </button>

        {profileOpen && user && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setProfileOpen(false)} />
            <div className="absolute bottom-12 left-3 z-50 w-56 rounded-xl border border-line bg-canvas p-3 shadow-2xl animate-fade-in">
              <div className="mb-2 flex items-center gap-2">
                <Avatar person={{ id: user.uid, name: user.name, avatarUrl: null }} size={40} />
                <div className="min-w-0">
                  <div className="truncate text-[13px] font-medium text-ink">{user.name}</div>
                  <div className="truncate text-[10px] text-faint">{user.email}</div>
                </div>
              </div>
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="flex w-full items-center justify-center gap-1.5 rounded-md border border-line px-2 py-1.5 text-xs text-muted transition hover:bg-hover hover:text-ink disabled:opacity-50"
              >
                {uploading ? 'Uploading…' : <><Icon i={Camera} size={14} /> Upload profile photo</>}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && void uploadPhoto(e.target.files[0])} />
              <p className="mt-1.5 text-[10px] leading-snug text-faint">Shows on your cards, comments, and everywhere your name appears (also updates Odoo).</p>
              {typeof Notification !== 'undefined' && (
                Notification.permission === 'granted' ? (
                  <p className="mt-2 flex items-start gap-1 text-[10px] text-brand"><Icon i={Check} size={14} /> Desktop notifications on — pings reach you even when this tab is in the background.</p>
                ) : Notification.permission === 'denied' ? (
                  <p className="mt-2 text-[10px] text-faint">Desktop notifications are blocked in your browser settings.</p>
                ) : (
                  <button
                    onClick={() => { void Notification.requestPermission().then(() => setProfileOpen(false)); }}
                    className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-line px-2 py-1.5 text-xs text-muted transition hover:bg-hover hover:text-ink"
                  >
                    <Icon i={Bell} size={14} /> Enable desktop notifications
                  </button>
                )
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
